// Page bitmaps (see render_bitmap.js) -> PDF, no dependencies.
//
// Geometry: the MPS-803 prints 480 dots across 8 inches (60 dpi) and feeds
// paper at 72 rows per inch, so one dot is 1.2 x 1 PDF points. Pages are US
// Letter (612 x 792 pt) with the 8 inch print area 18 pt from the left edge,
// as on a real printer with fanfold paper. Each printer page becomes one PDF
// page holding the dots as a 1-bit image mask (black where a pin fired).

export const PT_PER_DOT_X = 1.2;
export const PT_PER_DOT_Y = 1;
export const LEFT_MARGIN_PT = 18;
export const PAGE_WIDTH_PT = 612;

const enc = new TextEncoder();

async function deflate(bytes) {
  // FlateDecode is zlib-wrapped deflate, which is what 'deflate' produces.
  const stream = new Blob([bytes]).stream().pipeThrough(new CompressionStream('deflate'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

// 1 byte per pixel (1 = black) -> packed bits, rows padded to whole bytes, MSB first.
export function packBits(bm) {
  const rowBytes = Math.ceil(bm.width / 8);
  const out = new Uint8Array(rowBytes * bm.height);
  for (let y = 0; y < bm.height; y++) {
    for (let x = 0; x < bm.width; x++) {
      if (bm.data[y * bm.width + x]) out[y * rowBytes + (x >> 3)] |= 0x80 >> (x & 7);
    }
  }
  return out;
}

// opts: { widthPt, leftMarginPt } (default US Letter: 612 pt wide, print area 18 pt
// from the left). paperPdfOptions() gives the right values for the page lengths
// the web page offers.
//
// pages: 1-bit {width, height, data} (one byte per dot, 1 = black) or needle
// images from render_needle.js ({width, height, ptPerPxX, ptPerPxY, gray}).
// pageRows: height of every PDF page in points (= dot rows). Returns the PDF.
export async function buildPdf(pages, pageRows = 792, opts = {}) {
  const widthPt = opts.widthPt ?? PAGE_WIDTH_PT;
  const leftMarginPt = opts.leftMarginPt ?? LEFT_MARGIN_PT;
  const chunks = [];
  const offsets = []; // byte offset of object n+1
  let length = 0;
  const write = (data) => {
    const b = typeof data === 'string' ? enc.encode(data) : data;
    chunks.push(b);
    length += b.length;
  };
  const startObj = (n) => {
    offsets[n] = length;
    write(`${n} 0 obj\n`);
  };

  // Object layout: 1 catalog, 2 page tree, then per page: page, content, image.
  write('%PDF-1.4\n%\xE2\xE3\xCF\xD3\n');
  startObj(1);
  write('<< /Type /Catalog /Pages 2 0 R >>\nendobj\n');

  const kids = pages.map((_, i) => `${3 + i * 3} 0 R`).join(' ');
  startObj(2);
  write(`<< /Type /Pages /Kids [${kids}] /Count ${pages.length} >>\nendobj\n`);

  for (let i = 0; i < pages.length; i++) {
    const bm = pages[i];
    const page = 3 + i * 3;
    const content = page + 1;
    const image = page + 2;
    const gray = bm.gray !== undefined;
    const w = bm.width * (bm.ptPerPxX ?? PT_PER_DOT_X);
    const h = bm.height * (bm.ptPerPxY ?? PT_PER_DOT_Y);
    const y = pageRows - h; // top aligned

    startObj(page);
    write(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${widthPt} ${pageRows}] ` +
        `/Resources << /XObject << /Im0 ${image} 0 R >> >> /Contents ${content} 0 R >>\nendobj\n`,
    );

    const draw = enc.encode(`q 0 g ${w} 0 0 ${h} ${leftMarginPt} ${y} cm /Im0 Do Q\n`);
    startObj(content);
    write(`<< /Length ${draw.length} >>\nstream\n`);
    write(draw);
    write('\nendstream\nendobj\n');

    const packed = await deflate(gray ? bm.gray : packBits(bm));
    startObj(image);
    // 1-bit dots: ImageMask with /Decode [1 0], sample 1 paints the current
    // colour (black). Needle images: plain 8-bit DeviceGray, 255 = paper.
    const kind = gray
      ? '/ColorSpace /DeviceGray /BitsPerComponent 8'
      : '/ImageMask true /BitsPerComponent 1 /Decode [1 0]';
    write(
      `<< /Type /XObject /Subtype /Image /Width ${bm.width} /Height ${bm.height} ` +
        `${kind} /Filter /FlateDecode /Length ${packed.length} >>\nstream\n`,
    );
    write(packed);
    write('\nendstream\nendobj\n');
  }

  const count = 3 + pages.length * 3;
  const xref = length;
  write(`xref\n0 ${count}\n0000000000 65535 f \n`);
  for (let n = 1; n < count; n++) write(`${String(offsets[n]).padStart(10, '0')} 00000 n \n`);
  write(`trailer\n<< /Size ${count} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`);

  const out = new Uint8Array(length);
  let o = 0;
  for (const c of chunks) {
    out.set(c, o);
    o += c.length;
  }
  return out;
}

// A4 is 842 rows long (297 mm at 72 rows per inch) and 595.28 pt wide (210 mm).
// The 8 inch print area (576 pt) is centred on it; any other page length keeps
// the US Letter width (612 pt) with the 18 pt left margin.
export const A4_ROWS = 842;
export function paperPdfOptions(pageRows) {
  if (pageRows === A4_ROWS) {
    const widthPt = 595.28;
    return { widthPt, leftMarginPt: Math.round(((widthPt - 480 * PT_PER_DOT_X) / 2) * 100) / 100 };
  }
  return {};
}
